/**
 * Plant Node Component
 *
 * Custom React Flow node component for rendering plant equipment
 * with inlet/outlet ports and visual styling based on component type.
 */

import { memo, useMemo } from 'react';
import { Handle, Position, type NodeProps } from 'reactflow';
import type { PlantNodeData, PlantLoopSide } from './types';
import type { FluidMedium, PortDirection } from '../hvac/types';
import { getPlantComponentDefinition } from './components';

/**
 * Get handle position based on port position value
 */
function getHandleStyle(position: number, direction: PortDirection): React.CSSProperties {
  const side = direction === 'inlet' ? 'left' : 'right';
  return {
    top: `${String(position * 100)}%`,
    [side]: -8,
    width: 14,
    height: 14,
    borderRadius: '50%',
    border: '2px solid white',
  };
}

/**
 * Get handle color based on fluid medium
 */
function getMediumColor(medium: FluidMedium): string {
  switch (medium) {
    case 'water':
      return '#1976D2';
    case 'steam':
      return '#BDBDBD';
    default:
      return '#90A4AE';
  }
}

/**
 * Get loop side indicator color
 */
function getLoopSideColor(side: PlantLoopSide): string {
  return side === 'supply' ? '#4CAF50' : '#FF9800';
}

/**
 * Plant equipment icons
 */
function PlantIcon({ icon, color }: { icon: string; color: string }) {
  const commonProps = {
    className: 'h-6 w-6',
    fill: color,
  };

  switch (icon) {
    case 'chiller':
      return (
        <svg viewBox="0 0 24 24" {...commonProps}>
          <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zm-7-2h2v-4h-2v4zm-4 0h2v-4H8v4zm8 0h2v-4h-2v4z" />
          <path d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zm0 8c-1.65 0-3-1.35-3-3s1.35-3 3-3 3 1.35 3 3-1.35 3-3 3z" fillOpacity="0.5" />
        </svg>
      );

    case 'boiler':
      return (
        <svg viewBox="0 0 24 24" {...commonProps}>
          <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14z" />
          <path d="M12 6c-2 0-3.5 1.5-3.5 3.5 0 1.5 1 2.5 2 3.5.8.8 1.5 1.5 1.5 2.5h2c0-1.5-.7-2.3-1.5-3.2-1-.9-2-1.8-2-2.8 0-1.1.9-2 2-2s2 .9 2 2h1.5c0-2-1.5-3.5-3.5-3.5z" fillOpacity="0.8" />
        </svg>
      );

    case 'tower':
      return (
        <svg viewBox="0 0 24 24" {...commonProps}>
          <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14z" />
          <path d="M7 17h10v-2H7v2zm0-4h10v-2H7v2zm0-4h10V7H7v2z" fillOpacity="0.6" />
        </svg>
      );

    case 'pump':
      return (
        <svg viewBox="0 0 24 24" {...commonProps}>
          <circle cx="12" cy="12" r="8" fill="none" stroke={color} strokeWidth="2" />
          <path d="M12 6v6l4 2" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" />
        </svg>
      );

    case 'pump-headered':
      return (
        <svg viewBox="0 0 24 24" {...commonProps}>
          <circle cx="8" cy="12" r="5" fill="none" stroke={color} strokeWidth="1.5" />
          <circle cx="16" cy="12" r="5" fill="none" stroke={color} strokeWidth="1.5" />
          <path d="M8 9v3l2 1M16 9v3l2 1" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      );

    case 'heat-exchanger':
      return (
        <svg viewBox="0 0 24 24" {...commonProps}>
          <rect x="4" y="6" width="16" height="12" rx="2" fill="none" stroke={color} strokeWidth="2" />
          <path d="M8 6v12M12 6v12M16 6v12" fill="none" stroke={color} strokeWidth="1" strokeOpacity="0.5" />
        </svg>
      );

    case 'ground-loop':
      return (
        <svg viewBox="0 0 24 24" {...commonProps}>
          <path d="M4 18h16v2H4v-2z" />
          <path d="M8 8v8M12 6v10M16 8v8" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" />
          <path d="M8 8c0-2 8-2 8 0M8 16c0 2 8 2 8 0" fill="none" stroke={color} strokeWidth="2" />
        </svg>
      );

    case 'fluid-cooler':
      return (
        <svg viewBox="0 0 24 24" {...commonProps}>
          <rect x="5" y="4" width="14" height="16" rx="1" fill="none" stroke={color} strokeWidth="2" />
          <path d="M8 8h8M8 12h8M8 16h8" fill="none" stroke={color} strokeWidth="1.5" strokeOpacity="0.5" />
        </svg>
      );

    case 'splitter':
      return (
        <svg viewBox="0 0 24 24" {...commonProps}>
          <path d="M4 12h6M10 12l6-6M10 12l6 6M16 6h4M16 18h4" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" />
        </svg>
      );

    case 'mixer':
      return (
        <svg viewBox="0 0 24 24" {...commonProps}>
          <path d="M4 6h4M4 18h4M8 6l6 6M8 18l6-6M14 12h6" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" />
        </svg>
      );

    case 'pipe':
      return (
        <svg viewBox="0 0 24 24" {...commonProps}>
          <path d="M4 12h16" fill="none" stroke={color} strokeWidth="4" strokeLinecap="round" />
        </svg>
      );

    case 'coil-cooling':
      return (
        <svg viewBox="0 0 24 24" {...commonProps}>
          <rect x="6" y="4" width="12" height="16" rx="1" fill="none" stroke={color} strokeWidth="2" />
          <path d="M9 8h6M9 12h6M9 16h6" fill="none" stroke={color} strokeWidth="1.5" />
          <circle cx="12" cy="12" r="2" fill={color} fillOpacity="0.3" />
        </svg>
      );

    case 'coil-heating':
      return (
        <svg viewBox="0 0 24 24" {...commonProps}>
          <rect x="6" y="4" width="12" height="16" rx="1" fill="none" stroke={color} strokeWidth="2" />
          <path d="M9 8h6M9 12h6M9 16h6" fill="none" stroke={color} strokeWidth="1.5" />
          <path d="M12 9c0 1 2 1 2 2s-2 1-2 2" fill="none" stroke={color} strokeWidth="1.5" />
        </svg>
      );

    case 'water-heater':
      return (
        <svg viewBox="0 0 24 24" {...commonProps}>
          <ellipse cx="12" cy="6" rx="6" ry="2" fill="none" stroke={color} strokeWidth="2" />
          <path d="M6 6v12c0 1.1 2.69 2 6 2s6-.9 6-2V6" fill="none" stroke={color} strokeWidth="2" />
          <path d="M10 12c0 1 4 1 4 0M10 15c0 1 4 1 4 0" fill="none" stroke={color} strokeWidth="1.5" strokeOpacity="0.5" />
        </svg>
      );

    default:
      return (
        <svg viewBox="0 0 24 24" {...commonProps}>
          <rect x="4" y="4" width="16" height="16" rx="2" />
        </svg>
      );
  }
}

/**
 * Plant Node Component
 */
function PlantNodeComponent({ data, selected }: NodeProps<PlantNodeData>) {
  const definition = useMemo(
    () => getPlantComponentDefinition(data.componentType),
    [data.componentType]
  );

  if (!definition) {
    return (
      <div className="rounded-lg border-2 border-red-500 bg-red-100 p-3">
        <span className="text-red-700">Unknown component: {data.componentType}</span>
      </div>
    );
  }

  const inletPorts = definition.ports.filter((p) => p.direction === 'inlet');
  const outletPorts = definition.ports.filter((p) => p.direction === 'outlet');

  return (
    <div
      className={`
        relative min-w-[150px] rounded-lg border-2 bg-white shadow-md transition-shadow
        ${selected ? 'border-blue-500 shadow-lg ring-2 ring-blue-300' : 'border-gray-300'}
        ${data.hasErrors ? 'border-red-500' : ''}
      `}
      style={{
        borderTopColor: definition.color,
        borderTopWidth: 4,
      }}
    >
      {/* Loop side indicator */}
      <div
        className="absolute -left-1 top-2 h-3 w-3 rounded-full border-2 border-white shadow"
        style={{ backgroundColor: getLoopSideColor(data.loopSide) }}
        title={data.loopSide === 'supply' ? 'Supply Side' : 'Demand Side'}
      />

      {/* Inlet Handles */}
      {inletPorts.map((port) => (
        <Handle
          key={port.id}
          id={port.id}
          type="target"
          position={Position.Left}
          style={{
            ...getHandleStyle(port.position, 'inlet'),
            backgroundColor: getMediumColor(port.medium),
          }}
          title={`${port.name} (${port.medium})`}
        />
      ))}

      {/* Outlet Handles */}
      {outletPorts.map((port) => (
        <Handle
          key={port.id}
          id={port.id}
          type="source"
          position={Position.Right}
          style={{
            ...getHandleStyle(port.position, 'outlet'),
            backgroundColor: getMediumColor(port.medium),
          }}
          title={`${port.name} (${port.medium})`}
        />
      ))}

      {/* Header */}
      <div
        className="flex items-center gap-2 border-b px-3 py-2"
        style={{ backgroundColor: `${definition.color}15` }}
      >
        <PlantIcon icon={definition.icon} color={definition.color} />
        <div className="flex-1 overflow-hidden">
          <div className="truncate text-sm font-medium text-gray-900" title={data.name}>
            {data.name}
          </div>
          <div className="truncate text-xs text-gray-500">{definition.displayName}</div>
        </div>
      </div>

      {/* Ports summary */}
      <div className="px-3 py-1.5 text-xs text-gray-500">
        <div className="flex justify-between">
          <span>{inletPorts.length} in</span>
          <span className="mx-1 text-gray-300">|</span>
          <span>{outletPorts.length} out</span>
        </div>
      </div>

      {/* Error indicator */}
      {data.hasErrors && data.errors.length > 0 && (
        <div className="border-t border-red-200 bg-red-50 px-3 py-1">
          <div className="text-xs text-red-600">{data.errors[0]}</div>
        </div>
      )}
    </div>
  );
}

export const PlantNode = memo(PlantNodeComponent);
